import { Space } from "antd";
import { type TooltipPlacement } from "antd/lib/tooltip";
import { type MenuClickEventHandler } from "rc-menu/lib/interface";
import { useState } from "react";

import { BTDropdown } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { BdsIconCircleNotch } from "@buildertrend/components";

import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { useRequiredContext } from "helpers/globalContext/useRequiredContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { reloadQualtrics } from "utilities/thirdPartyIntegrations/qualtrics/qualtrics";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import { BTIconExternalLink } from "commonComponents/btWrappers/BTIcon";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";
import { MainNavLink } from "commonComponents/utilities/MainNavigation/common/MainNavLink";
import MainNavTooltip from "commonComponents/utilities/MainNavigation/common/MainNavTooltip";
import { type NavMenuItemResponse } from "commonComponents/utilities/MainNavigation/MainNavigation.api.types";
import {
    type IBTSubMenuItemProps,
    type IMenuTabInfo,
    menuItemClass,
    MenuOnClickTypes,
} from "commonComponents/utilities/MainNavigation/MainNavigation.types";
import { ShowOnPortal } from "commonComponents/utilities/ShowOnPortal/ShowOnPortal";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import "./MainNavDropdown.less";

interface IExpandedMainNavProps {
    customMenuContent?: React.ReactNode;
    iconContent?: React.ReactNode;
    tooltip?: string;
    tooltipPlacement?: TooltipPlacement;
    onVisibleChange?: (visible: boolean) => void;
    visible?: boolean;
    children?: React.ReactNode;
}

const qualtricsProxyClickHandler = () => {
    // Accessing event from window because the menu item does not give us access to the event.
    // We need to stop the default click event, locate the link that Qualtrics is injecting, and click that instead.
    const e = window.event as Event;
    const el = document.querySelector("#giveFeedback > a") as HTMLAnchorElement;
    if (e.target !== el) {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        e?.stopImmediatePropagation?.();

        if (el) {
            el.click();
        }
    }
};

export const MainNavDropdown: React.FunctionComponent<
    IBTSubMenuItemProps & IExpandedMainNavProps & ITrackingProp
> = track((props) => ({
    element: "MainNavDropdown",
    uniqueId: props["data-testid"],
}))(({ tracking, ...props }) => {
    const [dropdownVisible, setDropdownVisible] = useState(false);
    const dropdownVisibleValue = props.visible ?? dropdownVisible;
    const builderInfo = useRequiredContext(BuilderInfoContext);
    const userInfo = useRequiredContext(UserInfoContext);
    const dataForCurrentMenuItem = props.data[props.itemKey];
    const info = props.menuInfo[props.itemKey] as unknown as Record<string, IMenuTabInfo>;

    if (!dataForCurrentMenuItem) {
        return null;
    }
    const orderedKeys = Object.keys(info);
    let dropdownItems = Object.keys(dataForCurrentMenuItem)
        .filter((k) => info[k] !== undefined && k !== "title")
        .filter((k) => {
            const item = dataForCurrentMenuItem[k] as NavMenuItemResponse;
            const infoItem = info[k] as IMenuTabInfo | string;
            if (typeof infoItem === "string") return false; // skip testid fields
            const showInTemplateMode = infoItem.showInTemplateMode?.(
                props.builderInfo,
                props.templateSelectionsState,
            );
            const showOnPortal = infoItem.showOnPortal?.includes(props.loginType);

            return (
                item.visible &&
                (props.isTemplateMode ? showInTemplateMode : true) &&
                (infoItem.showOnPortal ? showOnPortal : true)
            );
        })
        .sort((a, b) => orderedKeys.indexOf(a) - orderedKeys.indexOf(b));

    if (dropdownItems.length === 0) {
        return null;
    }

    // move upsell tabs to the end
    dropdownItems = [
        ...dropdownItems.filter(
            (item) =>
                (
                    dataForCurrentMenuItem[
                        item as keyof typeof dataForCurrentMenuItem
                    ] as unknown as NavMenuItemResponse
                ).requiredPackage === null,
        ),
        ...dropdownItems.filter(
            (item) =>
                (
                    dataForCurrentMenuItem[
                        item as keyof typeof dataForCurrentMenuItem
                    ] as unknown as NavMenuItemResponse
                ).requiredPackage !== null,
        ),
    ];

    let menuTitle = dataForCurrentMenuItem.title;
    if (props.isTemplateMode && menuTitle) {
        menuTitle = menuTitle.replace("Job", "Template");
    }

    const hideUpgradeText = menuTitle === "Reports";

    // remove notifications dropdown item since it fully renders in customMenuContent
    dropdownItems = dropdownItems.filter((item) => item !== "notifications");

    const dropdownItemRecord = info[dropdownItems[0]];
    const navMenuItem = dataForCurrentMenuItem[
        dropdownItems[0] as keyof typeof dataForCurrentMenuItem
    ] as NavMenuItemResponse;

    const refreshQualtrics = () => {
        const el = document.querySelector("#giveFeedback > a") as HTMLAnchorElement;
        if (!el) {
            // Qualtrics intercept link has not been applied yet, so reload to re-evaluate
            reloadQualtrics();
        }
    };

    const handleVisibleChange = (visible: boolean) => {
        setDropdownVisible(visible);
        props.onVisibleChange?.(visible);

        if (visible) {
            refreshQualtrics();
        }
    };

    const handleMenuClick: MenuClickEventHandler = () => {
        setDropdownVisible(false);
    };

    const onContentClickCloseMenu = (menuTabInfo: IMenuTabInfo, menuItem: NavMenuItemResponse) => {
        setDropdownVisible(false);
        tracking?.trackEvent({ event: "MenuItemClick" });
        props.onContentClick?.(menuTabInfo, menuItem);
    };

    const button = (
        <BTButtonDeprecated
            data-testid={props["data-testid"] ?? ""}
            data-intercom-target={props["intercomTarget"]}
            aria-label={props["aria-label"]}
            noShadow
            className={props["className"]}
            onClick={() => handleVisibleChange(!dropdownVisibleValue)}
        >
            {props.children}
        </BTButtonDeprecated>
    );

    return props.children ? (
        <BTDropdown
            testid={props["data-testid"] ?? "main-nav-dropdown"}
            className="MainNavDropdown"
            getPopupContainer={(trigger) => trigger.parentElement!}
            destroyPopupOnHide={props.destroyPopupOnHide}
            visible={dropdownVisibleValue}
            onVisibleChange={handleVisibleChange}
            overlay={
                props.customMenuContent ? (
                    <>{props.customMenuContent}</>
                ) : (
                    <BTMenu testid="mainNavDropdownMenu" onClick={handleMenuClick}>
                        {dropdownItems.map((dropdownItem) => {
                            const dropdownItemRecord: IMenuTabInfo = info[dropdownItem];
                            const navMenuItem = dataForCurrentMenuItem[
                                dropdownItem as keyof typeof dataForCurrentMenuItem
                            ] as unknown as NavMenuItemResponse;

                            const disabledClass =
                                dropdownItemRecord.getIsDisabled?.(userInfo, builderInfo) ||
                                (dropdownItemRecord.enableForSingleJobOnly?.(builderInfo) &&
                                    props.jobId <= 0)
                                    ? "MenuItemDisabled"
                                    : undefined;

                            const menuTitle =
                                dropdownItemRecord.getTitle?.(
                                    navMenuItem.title,
                                    props.isTemplateMode,
                                ) ?? navMenuItem.title;
                            const menuTitleId = dropdownItem;
                            const menuItemContent = (
                                <MainNavLink
                                    info={dropdownItemRecord}
                                    data={props.data}
                                    navMenuItem={navMenuItem}
                                    jobId={props.jobId}
                                    isTemplateMode={props.isTemplateMode}
                                    loginType={props.loginType}
                                    onContentClick={onContentClickCloseMenu}
                                    aria-label={menuTitle}
                                    data-testid={dropdownItem}
                                    builderInfo={props.builderInfo}
                                    userInfo={props.userInfo}
                                    appDefaultInfo={props.appDefaultInfo}
                                    environmentInfo={props.envInfo}
                                >
                                    <div className="MainNavMenuItemContent">
                                        <Space size={4}>
                                            <>
                                                {dropdownItemRecord.icon}
                                                <div id={menuTitleId} style={{ height: "100%" }}>
                                                    {menuTitle || <BdsIconCircleNotch />}
                                                </div>
                                            </>
                                            {dropdownItemRecord.tag}
                                            <ShowOnPortal
                                                builder={navMenuItem.requiredPackage !== null}
                                                render={() => (
                                                    <StatusTagDisplay
                                                        statusType="info"
                                                        statusText="UPGRADE"
                                                    />
                                                )}
                                            />
                                        </Space>
                                        {dropdownItemRecord.onClickType?.(
                                            builderInfo,
                                            props.userInfo,
                                        ) === MenuOnClickTypes.External && <BTIconExternalLink />}
                                    </div>
                                </MainNavLink>
                            );

                            return (
                                <li
                                    data-testid={dropdownItem}
                                    key={dropdownItem}
                                    className={cn(disabledClass, "MainNavDropdownLineItem")}
                                    {...(dropdownItem === "giveFeedback"
                                        ? { onClick: qualtricsProxyClickHandler }
                                        : {})}
                                >
                                    <div className="MainNavMenuItemContainer">
                                        <div className={cn(menuItemClass, "flex-grow-1")}>
                                            {menuItemContent}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </BTMenu>
                )
            }
        >
            {props.tooltip ? (
                <MainNavTooltip
                    title={props.tooltip}
                    className="MainNavDropdown"
                    enabled={!dropdownVisibleValue}
                    placement={props.tooltipPlacement}
                >
                    {button}
                </MainNavTooltip>
            ) : (
                button
            )}
        </BTDropdown>
    ) : (
        <BTButtonDeprecated
            className="MainNavDropdown"
            data-testid={props["data-testid"] ?? ""}
            aria-label={props["aria-label"]}
            noShadow
        >
            <MainNavLink
                data-testid={dropdownItemRecord.testid}
                info={dropdownItemRecord}
                data={props.data}
                navMenuItem={navMenuItem}
                jobId={props.jobId}
                isTemplateMode={props.isTemplateMode}
                loginType={props.loginType}
                onContentClick={props.onContentClick}
                builderInfo={props.builderInfo}
                userInfo={props.userInfo}
                appDefaultInfo={props.appDefaultInfo}
            >
                <div className="MainNavMenuButton">
                    <Space size={4}>
                        {props.iconContent ?? <div>{menuTitle}</div>}
                        <ShowOnPortal
                            builder={navMenuItem.requiredPackage !== null}
                            render={() =>
                                !hideUpgradeText && (
                                    <StatusTagDisplay statusType="info" statusText="UPGRADE" />
                                )
                            }
                        />
                    </Space>
                </div>
            </MainNavLink>
        </BTButtonDeprecated>
    );
});
